import React from 'react';
import { Icons } from '../../../components/ui/Icons';
import { Twitter, Linkedin, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useReveal } from '../hooks/useScrollReveal';

interface Props { onEnterApp: () => void; }

const sections = [
  { title: 'Product', links: [{ label: 'Features', href: '#features' }, { label: 'How it works', href: '#how-it-works' }, { label: 'Pricing', href: '/pricing' }] },
  { title: 'Resources', links: [{ label: 'Contact', href: 'mailto:useneuraai@gmail.com' }] },
  { title: 'Legal', links: [{ label: 'Privacy', href: '/privacy-policy' }, { label: 'Terms', href: '/terms' }] },
];

const LandingFooter: React.FC<Props> = ({ onEnterApp }) => {
  const navigate = useNavigate();
  const ctaRef = useReveal();

  const handleClick = (e: React.MouseEvent, link: any) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      return;
    }

    if (link.href?.startsWith('/')) {
      e.preventDefault();
      navigate(link.href);
    } else if (link.href?.startsWith('#')) {
      e.preventDefault();
      document.getElementById(link.href.slice(1))?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* CTA Bumper — dramatic but white-respecting */}
      <section className="relative py-16 md:py-24 bg-black overflow-hidden">
        {/* Subtle color washes */}
        <div className="absolute top-0 left-[20%] w-[500px] h-[500px] bg-blue-600/[0.06] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-[20%] w-[400px] h-[400px] bg-purple-600/[0.06] rounded-full blur-[150px]" />

        <div ref={ctaRef} className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <div className="sr">
            <h2 className="text-[32px] md:text-[46px] font-semibold tracking-[-0.05em] text-white leading-[1.1] mb-5">
              Start shipping<br />faster, today
            </h2>
            <p className="text-[17px] text-white/30 font-normal max-w-md mx-auto mb-10 leading-relaxed">
              Join thousands using Neura to reclaim hours every week. Free forever — no credit card.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={onEnterApp} className="group bg-white text-black px-7 py-3.5 rounded-full text-[15px] font-semibold btn-primary flex items-center gap-2 justify-center">
                Get started free
                <svg className="w-4 h-4 opacity-40 group-hover:opacity-80 group-hover:translate-x-0.5 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <button onClick={() => navigate('/pricing')} className="text-white/40 border border-white/[0.08] px-7 py-3.5 rounded-full text-[15px] font-medium hover:text-white/60 hover:border-white/[0.15] transition-all">
                View pricing
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-black/[0.04] py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Icons.Logo className="w-7 h-7 rounded-lg" />
                <span className="text-[15px] font-bold tracking-tight text-black">Neura</span>
              </div>
              <p className="text-[13px] text-black/30 font-normal leading-relaxed max-w-[260px]">
                AI that makes you extraordinarily productive.
              </p>
            </div>
            {sections.map((s) => (
              <div key={s.title} className="flex flex-col gap-3">
                <h4 className="text-[11px] font-semibold text-black/50 tracking-[0.15em] uppercase">{s.title}</h4>
                <ul className="flex flex-col gap-2">
                  {s.links.map((l) => (
                    <li key={l.label}>
                      <a href={l.href || '#'} onClick={(e) => handleClick(e, l)} className="text-[13px] text-black/30 hover:text-black transition-colors font-normal">
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-black/[0.04] pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[12px] text-black/20 font-normal">© 2025 – 2026 Neura AI</p>
            <div className="flex items-center gap-2">
              {[
                { icon: Twitter, href: '#', label: 'X' },
                { icon: Linkedin, href: '#', label: 'LinkedIn' },
                { icon: Mail, href: 'mailto:useneuraai@gmail.com', label: 'Email' },
              ].map(({ icon: Ic, href, label }) => (
                <a key={label} href={href} className="w-7 h-7 rounded-full bg-black/[0.03] flex items-center justify-center text-black/20 hover:text-black hover:bg-black/[0.06] transition-all" aria-label={label}>
                  <Ic className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default LandingFooter;
